"use client"

import type React from "react"
import { CheckCircle2, Package } from "lucide-react"
import { ComplaintFilePreview } from "../complaint/ComplaintFilePreview.tsx"
import type { MaterialUsed } from "../inventory/materialUsageUtils.ts"
import { isStoredUploadPath, materialDisplayLabel, materialOutcomeLabel } from "../inventory/materialUsageUtils.ts"

type TaskCompletionSummaryProps = {
  taskId?: string
  status?: string
  completionNotes?: string | null
  completionProof?: string | null
  completedAt?: string | null
  materials?: MaterialUsed[] | null
}

export function TaskCompletionSummary({
  taskId,
  status,
  completionNotes,
  completionProof,
  completedAt,
  materials,
}: TaskCompletionSummaryProps) {
  const rows = materials ?? []
  const isCompleted = status === "completed"
  const hasProof = isStoredUploadPath(completionProof)
  const show =
    isCompleted || Boolean(completionNotes?.trim()) || rows.length > 0 || hasProof

  if (!show) return null

  return (
    <div className="space-y-3 border-t border-slate-200/80 pt-4">
      <div className="flex items-center gap-2">
        <CheckCircle2 className="h-4 w-4 text-emerald-green" aria-hidden />
        <h3 className="text-sm font-semibold text-deep-ocean">Completion record</h3>
      </div>

      {completedAt ? (
        <p className="text-xs text-slate-gray">
          Completed {new Date(completedAt).toLocaleString()}
        </p>
      ) : null}

      {completionNotes?.trim() ? (
        <div>
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-gray">
            Employee notes
          </p>
          <p className="rounded-lg border border-slate-200/80 bg-slate-50 px-3 py-2 text-sm text-slate-gray">
            {completionNotes}
          </p>
        </div>
      ) : null}

      <div>
        <p className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-gray">
          <Package className="h-3.5 w-3.5" aria-hidden />
          Inventory used
        </p>
        {rows.length === 0 ? (
          <p className="text-sm text-slate-gray">{isCompleted ? "None recorded" : "—"}</p>
        ) : (
          <ul className="space-y-1.5">
            {rows.map((row, index) => {
              const outcome = materialOutcomeLabel(row.usage_outcome)
              return (
                <li
                  key={`${row.item_type}-${index}`}
                  className="flex items-center justify-between gap-2 rounded-lg border border-slate-200/80 bg-white px-3 py-2"
                >
                  <span className="text-sm text-deep-ocean">{materialDisplayLabel(row)}</span>
                  {outcome ? (
                    <span
                      className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${
                        row.usage_outcome === "installed"
                          ? "bg-emerald-green/10 text-emerald-green"
                          : "bg-slate-gray/10 text-slate-gray"
                      }`}
                    >
                      {outcome}
                    </span>
                  ) : null}
                </li>
              )
            })}
          </ul>
        )}
        <p className="mt-1.5 text-[11px] text-slate-gray/80">
          Recorded from employee custody when the task was completed in the portal.
        </p>
      </div>

      {hasProof && taskId ? (
        <ComplaintFilePreview
          label="Completion proof"
          filePath={completionProof!}
          fetchUrl={`/tasks/${taskId}/completion-proof`}
        />
      ) : null}
    </div>
  )
}
